import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Spinner } from "react-bootstrap";

import {
  getFinancierasAdmin,
} from "../helpers/rutaFinancieras";

import "../css/financierasVigentes.css";

const clean = (v) =>
  String(v ?? "").trim();

const normalizar = (v) =>
  clean(v)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const inicialesFinanciera = (nombre = "") => {
  const palabras = clean(nombre)
    .split(/\s+/)
    .filter(Boolean);

  if (!palabras.length) {
    return "F";
  }

  return palabras
    .slice(0, 2)
    .map((p) =>
      p.charAt(0).toUpperCase()
    )
    .join("");
};

const formatFee = (valor) => {
  const n = Number(valor || 0);

  return `${n.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

const textoTipoCalculo = (tipo) => {
  switch (
    clean(tipo).toUpperCase()
  ) {
    case "MONTO_DIRECTO":
      return "Monto directo";

    case "POR_CUOTA":
      return "Según cuotas";

    case "FONDEO_INVERSO":
      return "Fondeo inverso";

    case "GROSS_UP":
    default:
      return "Gross-up";
  }
};

const FinancierasVigentes = () => {
  const [
    financieras,
    setFinancieras,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    q,
    setQ,
  ] = useState("");

  /*
    Arrancamos mostrando solamente
    las financieras ACTIVAS.

    Es lo más útil para un vendedor.
  */
  const [
    filtro,
    setFiltro,
  ] = useState("ACTIVAS");

  const [
    actualizado,
    setActualizado,
  ] = useState(null);

  /* ======================================================
     CARGAR
  ====================================================== */

  const cargarFinancieras =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const resp =
          await getFinancierasAdmin({
            page: 1,
            limit: 500,
            estado: "TODAS",
          });

        setFinancieras(
          Array.isArray(
            resp?.financieras
          )
            ? resp.financieras
            : []
        );

        setActualizado(
          new Date()
        );
      } catch (err) {
        console.error(
          "❌ Error cargando financieras:",
          err
        );

        setError(
          err?.response?.data
            ?.error ||
            err?.response?.data
              ?.message ||
            err?.message ||
            "No se pudieron cargar las financieras."
        );
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    cargarFinancieras();
  }, [cargarFinancieras]);

  /* ======================================================
     RESUMEN
  ====================================================== */

  const resumen =
    useMemo(() => {
      let activas = 0;
      let inactivas = 0;
      let conFee = 0;
      let sinTax = 0;
      let conCuotas = 0;

      financieras.forEach(
        (f) => {
          const activa =
            f?.activo !== false;

          const fee =
            Number(
              f?.feePorcentaje ||
                0
            );

          const aplicaTax =
            f?.aplicaTax !== false;

          const cuotas =
            Boolean(
              f?.requiereCuotas
            );

          if (activa) {
            activas += 1;
          } else {
            inactivas += 1;
          }

          if (fee > 0) {
            conFee += 1;
          }

          if (!aplicaTax) {
            sinTax += 1;
          }

          if (cuotas) {
            conCuotas += 1;
          }
        }
      );

      return {
        activas,
        inactivas,
        conFee,
        sinTax,
        conCuotas,
      };
    }, [financieras]);

  /* ======================================================
     FILTRADO
  ====================================================== */

  const financierasFiltradas =
    useMemo(() => {
      const busqueda =
        normalizar(q);

      return financieras
        .filter((f) => {
          const activa =
            f?.activo !== false;

          const fee =
            Number(
              f?.feePorcentaje ||
                0
            );

          const aplicaTax =
            f?.aplicaTax !== false;

          const tieneCuotas =
            Boolean(
              f?.requiereCuotas
            );

          if (
            filtro === "ACTIVAS" &&
            !activa
          ) {
            return false;
          }

          if (
            filtro === "INACTIVAS" &&
            activa
          ) {
            return false;
          }

          if (
            filtro === "CON_FEE" &&
            fee <= 0
          ) {
            return false;
          }

          if (
            filtro === "SIN_TAX" &&
            aplicaTax
          ) {
            return false;
          }

          if (
            filtro === "CON_CUOTAS" &&
            !tieneCuotas
          ) {
            return false;
          }

          if (!busqueda) {
            return true;
          }

          const texto =
            normalizar(
              [
                f?.financiera,
                f?.razonsocial,
                f?.codfinanciera,
              ]
                .filter(Boolean)
                .join(" ")
            );

          return texto.includes(
            busqueda
          );
        })
        .sort((a, b) => {
          const activaA =
            a?.activo !== false;

          const activaB =
            b?.activo !== false;

          /*
            Activas primero.
          */
          if (
            activaA !== activaB
          ) {
            return activaA
              ? -1
              : 1;
          }

          return clean(
            a?.financiera
          ).localeCompare(
            clean(
              b?.financiera
            ),
            "es",
            {
              sensitivity:
                "base",
            }
          );
        });
    }, [
      financieras,
      q,
      filtro,
    ]);

  const horaActualizacion =
    actualizado
      ? actualizado.toLocaleTimeString(
          "es-AR",
          {
            hour: "2-digit",
            minute: "2-digit",
          }
        )
      : "";

  /* ======================================================
     RENDER
  ====================================================== */

  return (
    <div className="fin-vista">
      <div className="fin-vista-header">
        <div>
          <h2>
            Financieras
          </h2>

          <p>
            Estado operativo actualizado
            para ventas y financiación.
          </p>
        </div>

        <div className="fin-vista-header-actions">
          {horaActualizacion && (
            <span className="fin-vista-updated">
              Actualizado:
              {" "}
              {horaActualizacion}
            </span>
          )}

          <button
            type="button"
            className="fin-vista-refresh"
            onClick={
              cargarFinancieras
            }
            disabled={loading}
          >
            ↻ Actualizar
          </button>
        </div>
      </div>

      {/* KPI */}

      <div className="fin-vista-kpis">
        <button
          type="button"
          className={`fin-vista-kpi activa ${
            filtro === "ACTIVAS"
              ? "selected"
              : ""
          }`}
          onClick={() =>
            setFiltro("ACTIVAS")
          }
        >
          <span>
            Activas
          </span>

          <strong>
            {resumen.activas}
          </strong>

          <small>
            Se pueden usar
          </small>
        </button>

        <button
          type="button"
          className={`fin-vista-kpi inactiva ${
            filtro === "INACTIVAS"
              ? "selected"
              : ""
          }`}
          onClick={() =>
            setFiltro(
              "INACTIVAS"
            )
          }
        >
          <span>
            Inactivas
          </span>

          <strong>
            {resumen.inactivas}
          </strong>

          <small>
            No utilizar
          </small>
        </button>

        <button
          type="button"
          className={`fin-vista-kpi ${
            filtro === "CON_FEE"
              ? "selected"
              : ""
          }`}
          onClick={() =>
            setFiltro("CON_FEE")
          }
        >
          <span>
            Con Fee
          </span>

          <strong>
            {resumen.conFee}
          </strong>

          <small>
            Tienen recargo
          </small>
        </button>

        <button
          type="button"
          className={`fin-vista-kpi ${
            filtro === "SIN_TAX"
              ? "selected"
              : ""
          }`}
          onClick={() =>
            setFiltro("SIN_TAX")
          }
        >
          <span>
            Sin Tax
          </span>

          <strong>
            {resumen.sinTax}
          </strong>

          <small>
            No aplican Tax
          </small>
        </button>

        <button
          type="button"
          className={`fin-vista-kpi ${
            filtro === "CON_CUOTAS"
              ? "selected"
              : ""
          }`}
          onClick={() =>
            setFiltro(
              "CON_CUOTAS"
            )
          }
        >
          <span>
            Con cuotas
          </span>

          <strong>
            {resumen.conCuotas}
          </strong>

          <small>
            Según plan
          </small>
        </button>
      </div>

      {/* BUSCADOR */}

      <div className="fin-vista-toolbar">
        <div className="fin-vista-search">
          <span>
            🔎
          </span>

          <input
            type="text"
            placeholder="Buscar financiera..."
            value={q}
            onChange={(e) =>
              setQ(
                e.target.value
              )
            }
          />

          {q && (
            <button
              type="button"
              onClick={() =>
                setQ("")
              }
            >
              ×
            </button>
          )}
        </div>

        <button
          type="button"
          className={
            filtro === "TODAS"
              ? "fin-vista-todas active"
              : "fin-vista-todas"
          }
          onClick={() =>
            setFiltro("TODAS")
          }
        >
          Ver todas
        </button>
      </div>

      {/* AVISO */}

      <div className="fin-vista-aviso">
        <span>
          ℹ
        </span>

        <div>
          <strong>
            Pantalla de consulta
          </strong>

          <span>
            Los vendedores pueden consultar
            la situación de cada financiera.
            No se puede modificar información
            desde esta pantalla.
          </span>
        </div>
      </div>

      {/* CONTENIDO */}

      <div className="fin-vista-card">
        {error && (
          <div className="fin-vista-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="fin-vista-loading">
            <Spinner
              animation="border"
              size="sm"
            />

            <span>
              Cargando
              financieras...
            </span>
          </div>
        ) : (
          <>
            <div className="fin-vista-scroll-help">
              En celular deslizá la tabla
              hacia los costados ← →
            </div>

            <div className="fin-vista-table-wrap">
              <table className="fin-vista-table">
                <thead>
                  <tr>
                    <th>
                      Financiera
                    </th>

                    <th className="text-center">
                      Estado
                    </th>

                    <th className="text-center">
                      Fee
                    </th>

                    <th className="text-center">
                      Tax
                    </th>

                    <th>
                      Cuotas
                    </th>

                    <th>
                      Modalidad
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {financierasFiltradas
                    .length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="fin-vista-empty"
                      >
                        No se encontraron
                        financieras.
                      </td>
                    </tr>
                  ) : (
                    financierasFiltradas.map(
                      (f) => {
                        const id =
                          f?._id ||
                          f?.id ||
                          f?.codfinanciera;

                        const activa =
                          f?.activo !==
                          false;

                        const fee =
                          Number(
                            f?.feePorcentaje ||
                              0
                          );

                        const aplicaTax =
                          f?.aplicaTax !==
                          false;

                        const reglas =
                          Array.isArray(
                            f?.reglasCuotas
                          )
                            ? f.reglasCuotas
                            : [];

                        const tieneCuotas =
                          Boolean(
                            f?.requiereCuotas
                          );

                        return (
                          <tr
                            key={id}
                            className={
                              activa
                                ? ""
                                : "fin-vista-row-inactive"
                            }
                          >
                            <td>
                              <div className="fin-vista-financiera">
                                <div className="fin-vista-logo">
                                  {f?.logo
                                    ?.url ? (
                                    <img
                                      src={
                                        f
                                          .logo
                                          .url
                                      }
                                      alt={
                                        clean(
                                          f
                                            .financiera
                                        ) ||
                                        "Financiera"
                                      }
                                    />
                                  ) : (
                                    <span>
                                      {inicialesFinanciera(
                                        f
                                          ?.financiera
                                      )}
                                    </span>
                                  )}
                                </div>

                                <div>
                                  <strong>
                                    {clean(
                                      f
                                        ?.financiera
                                    ) ||
                                      "-"}
                                  </strong>

                                  {f?.codfinanciera && (
                                    <small>
                                      Código{" "}
                                      {
                                        f
                                          .codfinanciera
                                      }
                                    </small>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="text-center">
                              <span
                                className={
                                  activa
                                    ? "fin-vista-status activa"
                                    : "fin-vista-status inactiva"
                                }
                              >
                                <span className="fin-vista-status-dot" />

                                {activa
                                  ? "ACTIVA"
                                  : "INACTIVA"}
                              </span>
                            </td>

                            <td className="text-center">
                              <strong
                                className={
                                  fee > 0
                                    ? "fin-vista-fee con-fee"
                                    : "fin-vista-fee"
                                }
                              >
                                {formatFee(
                                  fee
                                )}
                              </strong>
                            </td>

                            <td className="text-center">
                              <span
                                className={
                                  aplicaTax
                                    ? "fin-vista-tax si"
                                    : "fin-vista-tax no"
                                }
                              >
                                {aplicaTax
                                  ? "SÍ"
                                  : "NO"}
                              </span>
                            </td>

                            <td>
                              {tieneCuotas ? (
                                <div className="fin-vista-cuotas">
                                  {reglas.length >
                                  0 ? (
                                    reglas.map(
                                      (
                                        regla,
                                        index
                                      ) => (
                                        <span
                                          key={`${regla.cuotas}-${index}`}
                                        >
                                          {
                                            regla.cuotas
                                          }
                                          x
                                          {" · "}
                                          {formatFee(
                                            regla.feePorcentaje
                                          )}
                                        </span>
                                      )
                                    )
                                  ) : (
                                    <span>
                                      Requiere
                                      cuotas
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="fin-vista-no-aplica">
                                  No aplica
                                </span>
                              )}
                            </td>

                            <td>
                              <span className="fin-vista-calculo">
                                {textoTipoCalculo(
                                  f
                                    ?.tipoCalculo
                                )}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                    )
                  )}
                </tbody>
              </table>
            </div>

            <div className="fin-vista-footer">
              Mostrando{" "}
              <strong>
                {
                  financierasFiltradas.length
                }
              </strong>{" "}
              financiera
              {financierasFiltradas.length ===
              1
                ? ""
                : "s"}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FinancierasVigentes;